import { useAppDispatch, useAppSelector } from 'store/hooks';
import React from 'react';
import { Grid, Typography, Button, FormHelperText, Box } from '@mui/material';
import { Form } from 'formik';
import { MuiForm } from 'lib/Form';
import { AutocompleteFormField } from 'lib/form/fields/AutocompleteFormField';
import { getAllAudiences } from 'store/audiences/audiencesSlice';
import { CheckboxFormField } from 'lib/form/fields/CheckboxFormField';
import { Row } from 'ui/Flex';
import * as Yup from 'yup';
import { LoadingButton } from 'ui/buttons/LoadingButton';

interface PerkClubFormProps {
    open?: boolean;
    label?: string;
    clickPrevious?: () => void;
    onSubmit: (values: PerkAddEffectFormData) => void;
    isLoading: boolean;
    onClose: () => void;
}

export interface PerkAddEffectFormData {
    audienceIds: string[];
    grantPerkFromImpact: boolean;
    revokePerkFromImpact: boolean;
    validateCheckBoxes: string;
    grantPerkFromImpactInclude?: string;
    revokePerkFromImpactInclude?: string;
    grantPerkFromImpactExclude?: string;
    revokePerkFromImpactExclude?: string;
}

const initialValues: PerkAddEffectFormData = {
    audienceIds: [],
    grantPerkFromImpact: false,
    revokePerkFromImpact: false,
    validateCheckBoxes: '',
    grantPerkFromImpactInclude: '',
    revokePerkFromImpactInclude: '',
    grantPerkFromImpactExclude: '',
    revokePerkFromImpactExclude: ''
};

const validationSchema = Yup.object().shape({
    grantPerkFromImpact: Yup.boolean(),
    revokePerkFromImpact: Yup.boolean(),
    validateCheckBoxes: Yup.string().when(['revokePerkFromImpact', 'grantPerkFromImpact'], {
        is: (revokePerkFromImpact: boolean, grantPerkFromImpact: boolean) =>
            revokePerkFromImpact === false && grantPerkFromImpact === false,
        then: Yup.string().required('At least one checkbox should be checked.')
    }),
    audienceIds: Yup.array().min(1, 'At least one audience is required')
});

export const PerkClubForm: React.FC<PerkClubFormProps> = ({ open, onSubmit, onClose, isLoading }) => {
    const dispatch = useAppDispatch();
    const { audiences } = useAppSelector(state => state.audiences);

    const audiencesOptions = React.useMemo(
        () => audiences.map(item => ({ value: item._id, label: item.title })),
        [audiences]
    );

    React.useEffect(() => {
        dispatch(getAllAudiences());
    }, [dispatch]);

    return (
        <MuiForm onSubmit={onSubmit} initialValues={initialValues} validationSchema={validationSchema}>
            {({ submitForm, errors, values }) => {
                if (!open) {
                    return null;
                }
                return (
                    <Form>
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <Typography variant="h4">6. Add Triggers</Typography>
                                <Typography variant="body1">
                                    Your club perk has been successfully created. To configure, select an
                                    audience to associate with this perk.
                                </Typography>
                            </Grid>

                            <Grid size={12}>
                                <AutocompleteFormField
                                    name="audienceIds"
                                    label="Select Audience"
                                    options={audiencesOptions}
                                    multiple
                                />
                            </Grid>

                            <Grid container justifyContent="space-between" alignItems="center" size={12}>
                                <Typography variant="body1">
                                    Create trigger to grant my associated perk when users become an audience
                                    member
                                </Typography>
                                <Box marginRight={-1}>
                                    <CheckboxFormField name="grantPerkFromImpact" />
                                </Box>
                            </Grid>
                            {values?.grantPerkFromImpact && (
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6
                                    }}
                                >
                                    <AutocompleteFormField
                                        label="Include if user is in"
                                        options={audiencesOptions}
                                        name="grantPerkFromImpactInclude"
                                    />
                                </Grid>
                            )}
                            {values?.grantPerkFromImpact && (
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6
                                    }}
                                >
                                    <AutocompleteFormField
                                        label="Exclude if user is in"
                                        options={audiencesOptions}
                                        name="grantPerkFromImpactExclude"
                                    />
                                </Grid>
                            )}
                            <Grid container justifyContent="space-between" alignItems="center" size={12}>
                                <Typography variant="body1">
                                    Create trigger to revoke my associated perk when users are removed from an
                                    audience
                                </Typography>
                                <Box marginRight={-1}>
                                    <CheckboxFormField name="revokePerkFromImpact" />
                                </Box>
                            </Grid>
                            {values?.revokePerkFromImpact && (
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6
                                    }}
                                >
                                    <AutocompleteFormField
                                        label="Include if user is in"
                                        options={audiencesOptions}
                                        name="revokePerkFromImpactInclude"
                                    />
                                </Grid>
                            )}
                            {values?.revokePerkFromImpact && (
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6
                                    }}
                                >
                                    <AutocompleteFormField
                                        label="Exclude if user is in"
                                        options={audiencesOptions}
                                        name="revokePerkFromImpactExclude"
                                    />
                                </Grid>
                            )}
                            <Grid size={12}>
                                {errors.validateCheckBoxes && (
                                    <FormHelperText error>{errors.validateCheckBoxes}</FormHelperText>
                                )}
                            </Grid>
                            <Grid size={12}>
                                <Row align="flex-end" flex={1}>
                                    <Row gutter>
                                        <Button color="primary" onClick={onClose}>
                                            Cancel
                                        </Button>
                                        <LoadingButton
                                            onClick={submitForm}
                                            color="primary"
                                            variant="contained"
                                            loading={isLoading}
                                            disabled={isLoading}
                                        >
                                            Save & Close
                                        </LoadingButton>
                                    </Row>
                                </Row>
                            </Grid>
                        </Grid>
                    </Form>
                );
            }}
        </MuiForm>
    );
};
